import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import {
  selectFlightRoomById,
  useChannels,
  useChatMessage,
  useFlightRooms,
  useGetFlightRoomById,
  useResolveExternalRoom,
  useUsers,
} from '@connect-x-app/hybrid-state';
import {
  ApiError,
  FlightroomDeepLinkAction,
  HttpStatus,
  QueryKeys,
  ResponseErrorCodes,
} from '@connect-x-app/shared-domain-models';
import { hasOpenTasks } from '@connect-x-app/shared-utils';
import { Spinner, toastError } from '@connect-x-app/web-ui';
import { useMatchFlightroomRoute } from '@connect-x-app/web-utils';
import { useQueryClient } from '@tanstack/react-query';

import { Chat } from './Chat';
import { ChannelSelectionEmpty, ChatWrapperEmpty } from './components';

import './ChatWrapper.scss';

export const ChatWrapper = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const match = useMatchFlightroomRoute(location.pathname);
  const roomId = match?.params.roomId ?? null;
  const channelId = match?.params.channelId ?? null;
  const isChannelPath = !!match?.params.isChannelPath;

  const queryParams = new URLSearchParams(location.search);
  const externalMessageId = queryParams.get('externalMessageId');
  const externalRoomId = queryParams.get('externalRoomId') ?? undefined;
  const isJoinAction = useRef(queryParams.get('action') === FlightroomDeepLinkAction.JOIN);

  const {
    data: resolvedRoom,
    error: resolveError,
    isError: isResolveError,
    isLoading: isResolveLoading,
  } = useResolveExternalRoom(externalRoomId);
  const effectiveRoomId = roomId ?? resolvedRoom?.id ?? null;

  const { data: room, error, isError, isLoading, isSuccess } = useGetFlightRoomById(effectiveRoomId);
  const { closeFlightRoom, removeFlightRoom, selectedFlightRoom, updateFlightRoom } = useFlightRooms();
  const { closeChannel, openChannel, roomChannels, selectedChannel, setChannels } = useChannels();
  const { reset } = useChatMessage();
  const { updateUsers } = useUsers();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const navigateToFlightRooms = useCallback(() => {
    navigate('/dashboard/flightrooms');
  }, [navigate]);

  useEffect(() => {
    if (!externalRoomId || !resolvedRoom?.id || roomId) {
      return;
    }
    navigate(`/dashboard/flightrooms/${resolvedRoom.id}${location.search}`, { replace: true });
  }, [externalRoomId, resolvedRoom?.id, roomId, location.search, navigate]);

  useEffect(() => {
    if (effectiveRoomId && channelId) {
      openChannel(effectiveRoomId, channelId);
      reset();
    }
  }, [channelId, openChannel, reset, effectiveRoomId, selectedChannel?.id]);

  useEffect(() => {
    return () => {
      if (effectiveRoomId && channelId) {
        closeChannel(effectiveRoomId, channelId);
        queryClient.removeQueries({ queryKey: [QueryKeys.FLIGHTROOM] });
      }
    };
  }, [channelId, closeChannel, queryClient, effectiveRoomId]);

  useEffect(() => {
    if (!room || !isSuccess || room.closed || !room.mainChannel?.id || !effectiveRoomId) {
      return;
    }

    const mainChannelId = room.mainChannel.id;
    let canonicalPath: string | null = null;
    if (!channelId && !isChannelPath) {
      canonicalPath = `/dashboard/flightrooms/${effectiveRoomId}/${mainChannelId}`;
    } else if (isChannelPath && channelId === mainChannelId) {
      canonicalPath = `/dashboard/flightrooms/${effectiveRoomId}/${channelId}`;
    } else if (!isChannelPath && channelId && channelId !== mainChannelId) {
      canonicalPath = `/dashboard/flightrooms/${effectiveRoomId}/channel/${channelId}`;
    }

    if (canonicalPath) {
      const queryUrl = externalMessageId ? `?externalMessageId=${externalMessageId}` : '';
      navigate(`${canonicalPath}${queryUrl}`, {
        replace: true,
      });
    }
  }, [channelId, externalMessageId, isChannelPath, isSuccess, navigate, room, effectiveRoomId]);

  useEffect(() => {
    if (room && isSuccess && !room.closed) {
      const { customChannels, mainChannel, ...flightRoom } = room;
      updateFlightRoom(flightRoom);
      setChannels(room.id, { customChannels, mainChannel });
      updateUsers(mainChannel.users);
    }
  }, [isSuccess, room, setChannels, updateFlightRoom, updateUsers]);

  useEffect(() => {
    if (room?.closed) {
      removeFlightRoom(room.id);
      navigateToFlightRooms();
    }
  }, [removeFlightRoom, room, navigateToFlightRooms]);

  useEffect(() => {
    let apiError: ApiError | null = null;
    if (isError) {
      apiError = error;
    } else if (isResolveError) {
      apiError = resolveError;
    }

    if (!apiError) {
      return;
    }

    if (isJoinAction.current) {
      return;
    }

    if (apiError.statusCode === HttpStatus.NOT_FOUND) {
      toastError(t('NOTIFICATION_CENTER.ERRORS.ROOM_NOT_FOUND.TITLE'));
      if (effectiveRoomId) {
        removeFlightRoom(effectiveRoomId);
      }
      navigateToFlightRooms();
    } else if (apiError.statusCode === HttpStatus.FORBIDDEN) {
      if (apiError.error === ResponseErrorCodes.FLIGHT_ROOM_PENDING_JOIN) {
        toastError(t('FLIGHTROOM.NO_ACCESS_ALERT.MESSAGE.PENDING_ACCESS_REQUEST'));
      } else {
        // A room with an open task must stay in the store: the Tasks tab renders from it, and a
        // task-holder without join permission gets 403 on the room fetch (e.g. external-mention push).
        if (effectiveRoomId && !hasOpenTasks(selectFlightRoomById(effectiveRoomId)?.tasks)) {
          removeFlightRoom(effectiveRoomId);
        }
        toastError(t('FLIGHTROOM.NO_ACCESS_ALERT.MESSAGE.GENERAL'));
      }
      navigateToFlightRooms();
    }
  }, [isError, isResolveError, removeFlightRoom, effectiveRoomId, error, resolveError, t, navigateToFlightRooms]);

  // Offline fallback: when the API fails with no channelId in the URL, use cached channels or navigate away.
  // Skips 404/403 — those are already handled (toast + redirect) by the error effect above.
  useEffect(() => {
    if (!isError || channelId || isChannelPath || !effectiveRoomId) {
      return;
    }
    const apiError = error as ApiError | null;
    if (apiError?.statusCode === HttpStatus.NOT_FOUND || apiError?.statusCode === HttpStatus.FORBIDDEN) {
      return;
    }
    const mainChannelId = roomChannels?.mainChannel?.id;
    if (mainChannelId) {
      navigate(`/dashboard/flightrooms/${effectiveRoomId}/${mainChannelId}`, { replace: true });
    } else {
      toastError(t('TOAST_ERROR'));
      closeFlightRoom();
      navigateToFlightRooms();
    }
  }, [
    channelId,
    closeFlightRoom,
    effectiveRoomId,
    error,
    isChannelPath,
    isError,
    navigate,
    navigateToFlightRooms,
    removeFlightRoom,
    roomChannels,
    t,
  ]);

  const isAwaitingChannelSelection = !!selectedFlightRoom && !!channelId && !selectedChannel;

  if (isLoading || isResolveLoading || isAwaitingChannelSelection) {
    return (
      <div className="chat-wrapper__spinner">
        <Spinner size="large" />
      </div>
    );
  }

  if (isChannelPath && !channelId) {
    return <ChannelSelectionEmpty />;
  }

  if (!selectedFlightRoom) {
    return <ChatWrapperEmpty />;
  }

  return <Chat focusedMessageId={externalMessageId} />;
};
